import { useState } from 'react';
import { IconButton, Badge, Menu, Box, Typography, Divider, List, ListItemButton, ListItemText, Tooltip, Button, Chip } from '@mui/material';
import { NotificationsNoneOutlined } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notificationsApi } from '../api/engine.api';
import { EmptyState } from './EmptyState';

const PRIORITY_COLOR: Record<string, 'default' | 'info' | 'warning' | 'error'> = { low: 'default', normal: 'info', high: 'warning', urgent: 'error' };

/** Notification center — live unread badge + recent list + mark read. */
export function NotificationMenu() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  const unread = useQuery({ queryKey: ['notif-unread'], queryFn: notificationsApi.unreadCount, refetchInterval: 60_000 });
  const list = useQuery({ queryKey: ['notif-list'], queryFn: () => notificationsApi.list({ limit: 8 }), enabled: open });

  const refresh = () => { qc.invalidateQueries({ queryKey: ['notif-unread'] }); qc.invalidateQueries({ queryKey: ['notif-list'] }); };
  const markRead = useMutation({ mutationFn: (id: number) => notificationsApi.markRead(id), onSuccess: refresh });
  const markAll = useMutation({ mutationFn: () => notificationsApi.markAllRead(), onSuccess: refresh });

  const items = list.data?.data ?? [];

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" size="small" onClick={(e) => setAnchor(e.currentTarget)}>
          <Badge color="error" badgeContent={unread.data ?? 0} max={9} invisible={(unread.data ?? 0) === 0}>
            <NotificationsNoneOutlined />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={open} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} slotProps={{ paper: { sx: { width: 360, maxWidth: '92vw' } } }}>
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={700}>Notifications</Typography>
          {(unread.data ?? 0) > 0 && <Button size="small" onClick={() => markAll.mutate()}>Mark all read</Button>}
        </Box>
        <Divider />
        {items.length === 0 ? (
          <EmptyState title="You're all caught up" description="No notifications right now." dense />
        ) : (
          <List dense disablePadding sx={{ maxHeight: 380, overflowY: 'auto' }}>
            {items.map((n) => (
              <ListItemButton key={n.id} divider onClick={() => { if (!n.isRead) markRead.mutate(n.id); }} sx={{ bgcolor: n.isRead ? undefined : 'action.hover' }}>
                <ListItemText
                  primary={<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><span>{n.title}</span><Chip size="small" label={n.priority} color={PRIORITY_COLOR[n.priority] ?? 'default'} /></Box>}
                  secondary={n.body}
                  primaryTypographyProps={{ fontWeight: n.isRead ? 500 : 700, fontSize: 14 }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
        <Divider />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button size="small" onClick={() => { setAnchor(null); navigate('/notifications'); }}>View all</Button>
        </Box>
      </Menu>
    </>
  );
}
